import { ESTADOS_GUIA } from '@scm/contratos';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { entero, fechaHora } from '../lib/formato.js';
import { CLS_GUIA, txt, TXT_GUIA } from '../lib/enums.js';
import { columnasPresentes, ConmutadorVista, Kanban, useVista } from '../piezas/Kanban.jsx';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Guias entre almacenes.
 *
 * Las acciones que se ofrecen salen del ESTADO de la guia, no de un combo
 * fijo: una guia entregada no se vuelve a recibir y una generada no se puede
 * recibir sin haberse despachado. La base lo impide de todos modos; esto
 * evita ofrecer lo que va a ser rechazado.
 */

interface GuiaLista {
  folio: string;
  numero_guia: string;
  estado: string;
  paqueteria: string;
  url_rastreo: string | null;
  origen: string;
  destino: string;
  recibido_por: string | null;
  recibido_en: string | null;
  creado_en: string;
  tickets: number;
  consolidado: boolean;
}

interface Renglon {
  numero_parte: string;
  articulo: string;
  cantidad: number;
  series: string[];
}

interface Evento {
  estado: string;
  ocurrido_en: string;
  responsable: string | null;
  nota: string | null;
}

interface Detalle {
  guia: Omit<GuiaLista, 'tickets' | 'consolidado' | 'creado_en'>;
  contenido: Renglon[];
  tickets: { folio: string; cliente: string }[];
  linea_de_tiempo: Evento[];
}

export function Guias(): JSX.Element {
  const [vista, ponerVista] = useVista('guias');
  const [filas, setFilas] = useState<GuiaLista[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [soloVivas, setSoloVivas] = useState(true);
  /* En react-router 7 `navigate()` devuelve una promesa: la navegacion puede
     esperar a que se resuelvan los datos de la ruta destino. Aqui se descarta
     con `void` a proposito —es un click de fila, no hay nada que esperar— y
     se deja dicho para que quien necesite saber cuando termino la navegacion
     sepa que puede await-earla. */
  const navegar = useNavigate();

  useEffect(() => {
    api
      .obtener<GuiaLista[]>('/v1/guias')
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, []);

  /* El filtro es local porque la lista viene completa y acotada a cien: una
     ida al servidor para esconder las entregadas no compra nada. */
  const vistas =
    filas === null ? null : soloVivas ? filas.filter((g) => g.estado !== 'GUIA_ENTREGADA') : filas;

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <div className="chips">
          <button
            type="button"
            className={soloVivas ? 'chip on' : 'chip'}
            onClick={() => setSoloVivas(true)}
            data-prueba="filtro-vivas"
          >
            En curso
          </button>
          <button
            type="button"
            className={!soloVivas ? 'chip on' : 'chip'}
            onClick={() => setSoloVivas(false)}
          >
            Todas
          </button>
        </div>
        <ConmutadorVista vista={vista} poner={ponerVista} />
        <span className="crece" />
        {vistas && (
          <span className="muted" style={{ fontSize: 12 }} data-prueba="total">
            {vistas.length} {vistas.length === 1 ? 'guia' : 'guias'}
          </span>
        )}
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {vistas === null ? (
          <Cargando que="las guias" />
        ) : vistas.length === 0 ? (
          <Vacio
            mensaje={soloVivas ? 'No hay guias en curso' : 'Todavia no hay guias'}
            ayuda="Una guia nueva aparece aqui en cuanto se genera."
            icono="🚚"
          />
        ) : vista === 'kanban' ? (
          <Kanban
            items={vistas}
            estadoDe={(g) => g.estado}
            claveDe={(g) => g.folio}
            columnas={columnasPresentes(
              vistas.map((g) => g.estado),
              [{ estados: ESTADOS_GUIA }],
            )}
            etiquetas={TXT_GUIA}
            clases={CLS_GUIA}
            alAbrir={(g) => void navegar(`/guias/${g.folio}`)}
            tarjeta={(g) => (
              <>
                <div className="folio">{g.folio}</div>
                <b>{g.numero_guia ?? 'sin numero'}</b>
                <div className="kan-pie">
                  <span className="muted">{g.destino}</span>
                  <span className="muted">{g.tickets} tkt</span>
                </div>
              </>
            )}
          />
        ) : (
          <table data-prueba="tabla-guias">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Numero de guia</th>
                <th>Ruta</th>
                <th>Estado</th>
                <th className="num sec">Tickets</th>
                <th className="sec">Alta</th>
              </tr>
            </thead>
            <tbody>
              {vistas.map((g) => (
                <tr
                  key={g.folio}
                  className="elegible"
                  tabIndex={0}
                  role="button"
                  onClick={() => void navegar(`/guias/${g.folio}`)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      void navegar(`/guias/${g.folio}`);
                    }
                  }}
                >
                  <td className="folio">{g.folio}</td>
                  <td>
                    <span className="folio">{g.numero_guia}</span>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {g.paqueteria}
                    </div>
                  </td>
                  <td>
                    <span className="folio">{g.origen}</span> →{' '}
                    <span className="folio">{g.destino}</span>
                  </td>
                  <td>
                    <span className={`tag ${CLS_GUIA[g.estado] ?? 't-mute'}`}>
                      {txt(TXT_GUIA, g.estado)}
                    </span>
                    {/* Una guia con piezas de varios clientes se marca: al
                        recibirla hay que separar antes de dotar. */}
                    {g.consolidado && (
                      <span className="tag t-cold" style={{ marginLeft: 4 }}>
                        consolidada
                      </span>
                    )}
                  </td>
                  <td className="num sec">{entero(g.tickets)}</td>
                  <td className="sec">{fechaHora(g.creado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/** Que acciones admite una guia en cada estado. */
function accionesDe(estado: string): ('despachar' | 'recibir' | 'incidencia')[] {
  if (estado === 'GUIA_GENERADA') return ['despachar', 'incidencia'];
  if (estado === 'GUIA_RECOLECTADA' || estado === 'GUIA_TRANSITO') return ['recibir', 'incidencia'];
  return [];
}

export function Guia(): JSX.Element {
  const { folio = '' } = useParams();
  const { puede } = useSesion();
  const [d, setD] = useState<Detalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [pidiendo, setPidiendo] = useState<'recibir' | 'incidencia' | null>(null);
  const [quien, setQuien] = useState('');
  const [nota, setNota] = useState('');
  const [devolver, setDevolver] = useState(false);
  const escribe = puede('MOD_INVENTARIO', 'escribir');

  const cargar = useCallback(() => {
    api
      .obtener<Detalle>(`/v1/guias/${encodeURIComponent(folio)}`)
      .then(({ datos }) => setD(datos))
      .catch(setError);
  }, [folio]);

  useEffect(cargar, [cargar]);

  async function actuar(que: 'despachar' | 'recibir' | 'incidencia'): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      if (que === 'despachar') {
        const { datos } = await api.enviar<{ movimiento: string }>(
          `/v1/guias/${encodeURIComponent(folio)}/despachar`,
          {},
        );
        setAviso(`Despachada. Movimiento ${datos.movimiento}`);
      } else if (que === 'recibir') {
        const { datos } = await api.enviar<{ movimiento: string }>(
          `/v1/guias/${encodeURIComponent(folio)}/recibir`,
          { recibido_por: quien },
        );
        setAviso(`Recibida por ${quien}. Movimiento ${datos.movimiento}`);
      } else {
        const { datos } = await api.enviar<{ devolucion: string | null }>(
          `/v1/guias/${encodeURIComponent(folio)}/incidencia`,
          { nota, devolver_al_origen: devolver },
        );
        setAviso(
          datos.devolucion
            ? `Incidencia registrada y devuelta al origen. Entrada ${datos.devolucion}`
            : 'Incidencia registrada',
        );
      }
      setPidiendo(null);
      setQuien('');
      setNota('');
      setDevolver(false);
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  if (error !== null && d === null) {
    return (
      <>
        <Pagehead />
        <AvisoError error={error}>
          <div style={{ marginTop: 8 }}>
            <Link className="btn sm" to="/guias">
              ← Volver a las guias
            </Link>
          </div>
        </AvisoError>
      </>
    );
  }

  if (d === null) {
    return (
      <>
        <Pagehead />
        <div className="card">
          <Cargando que="la guia" />
        </div>
      </>
    );
  }

  const posibles = escribe ? accionesDe(d.guia.estado) : [];

  return (
    <>
      <Pagehead />
      {aviso && <Toast mensaje={aviso} />}
      {error !== null && <AvisoError error={error} />}

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <Link className="btn sm ghost" to="/guias">
          ← Guias
        </Link>
        <span className="crece" />
        {posibles.includes('despachar') && (
          <button
            type="button"
            className="btn pri"
            disabled={ocupado}
            onClick={() => void actuar('despachar')}
            data-prueba="despachar"
          >
            Despachar
          </button>
        )}
        {posibles.includes('recibir') && (
          <button
            type="button"
            className="btn pri"
            onClick={() => setPidiendo('recibir')}
            data-prueba="recibir"
          >
            Recibir
          </button>
        )}
        {posibles.includes('incidencia') && (
          <button
            type="button"
            className="btn danger"
            onClick={() => setPidiendo('incidencia')}
            data-prueba="incidencia"
          >
            Reportar incidencia
          </button>
        )}
      </div>

      {/* La recepcion pide el nombre de quien firma, y la incidencia su nota.
          No se mandan vacios: una recepcion sin nombre no sirve de evidencia
          y una incidencia sin nota no se puede reclamar a la paqueteria. */}
      {pidiendo === 'recibir' && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-h">
            <h2>Recibir la guia {d.guia.numero_guia}</h2>
          </div>
          <div className="campo">
            <label htmlFor="quien">Recibido por</label>
            <input
              id="quien"
              value={quien}
              onChange={(ev) => setQuien(ev.target.value)}
              placeholder="Nombre de quien firma"
              data-prueba="recibido-por"
            />
            <span className="ayuda">Queda en la bitacora como la persona que firmo.</span>
          </div>
          <div className="fila" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn ghost" onClick={() => setPidiendo(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn pri"
              disabled={ocupado || quien.trim().length < 3}
              onClick={() => void actuar('recibir')}
              data-prueba="confirmar"
            >
              Confirmar recepcion
            </button>
          </div>
        </div>
      )}

      {pidiendo === 'incidencia' && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-h">
            <h2>Incidencia en la guia {d.guia.numero_guia}</h2>
          </div>
          <div className="campo">
            <label htmlFor="nota">Que paso</label>
            <textarea
              id="nota"
              value={nota}
              onChange={(ev) => setNota(ev.target.value)}
              placeholder="Caja abierta, dos piezas faltantes…"
              data-prueba="nota"
            />
          </div>
          <div className="campo">
            <label htmlFor="devolver">Devolver al origen</label>
            <select
              id="devolver"
              value={devolver ? 'si' : 'no'}
              onChange={(ev) => setDevolver(ev.target.value === 'si')}
            >
              <option value="no">No, las piezas siguen en transito</option>
              <option value="si">Si, las piezas regresan al almacen de origen</option>
            </select>
            <span className="ayuda">
              Devolver genera la entrada al ORIGEN: la pieza nunca llego al destino.
            </span>
          </div>
          <div className="fila" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn ghost" onClick={() => setPidiendo(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn danger"
              disabled={ocupado || nota.trim().length < 5}
              onClick={() => void actuar('incidencia')}
              data-prueba="confirmar"
            >
              Registrar incidencia
            </button>
          </div>
        </div>
      )}

      <div className="grid r2">
        <div className="card">
          <div className="card-h">
            <h2>Guia {d.guia.folio}</h2>
            <span className={`tag ${CLS_GUIA[d.guia.estado] ?? 't-mute'}`}>
              {txt(TXT_GUIA, d.guia.estado)}
            </span>
          </div>
          <dl className="datos">
            <div className="dato">
              <dt>Numero de guia</dt>
              <dd className="folio">
                {/* La liga la arma el catalogo de paqueterias con su
                    plantilla. `noreferrer` porque es un sitio de tercero. */}
                {d.guia.url_rastreo ? (
                  <a href={d.guia.url_rastreo} target="_blank" rel="noreferrer noopener">
                    {d.guia.numero_guia} ↗
                  </a>
                ) : (
                  d.guia.numero_guia
                )}
              </dd>
            </div>
            <div className="dato">
              <dt>Paqueteria</dt>
              <dd>{d.guia.paqueteria}</dd>
            </div>
            <div className="dato">
              <dt>Origen</dt>
              <dd className="folio">{d.guia.origen}</dd>
            </div>
            <div className="dato">
              <dt>Destino</dt>
              <dd className="folio">{d.guia.destino}</dd>
            </div>
            {d.guia.recibido_por && (
              <div className="dato">
                <dt>Recibido por</dt>
                <dd>
                  {d.guia.recibido_por}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {fechaHora(d.guia.recibido_en)}
                  </div>
                </dd>
              </div>
            )}
          </dl>

          {d.tickets.length > 0 && (
            <>
              <div className="sec-title" style={{ marginTop: 14 }}>
                Tickets que surte
              </div>
              <div className="chips">
                {d.tickets.map((t) => (
                  <Link key={t.folio} className="chip" to={`/tickets/${t.folio}`}>
                    {t.folio} · {t.cliente}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="card">
          <div className="card-h">
            <h2>Linea de tiempo</h2>
          </div>
          {d.linea_de_tiempo.length === 0 ? (
            <Vacio mensaje="Sin eventos todavia" icono="🕑" />
          ) : (
            <ol className="tl">
              {d.linea_de_tiempo.map((e, i) => (
                <li className="tl-it" key={`${e.estado}-${e.ocurrido_en}-${i}`}>
                  <b>{txt(TXT_GUIA, e.estado)}</b>
                  <div className="muted" style={{ fontSize: 11.5 }}>
                    {fechaHora(e.ocurrido_en)}
                    {e.responsable && <> · {e.responsable}</>}
                  </div>
                  {e.nota && <div style={{ fontSize: 12, marginTop: 2 }}>{e.nota}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="card p0" style={{ marginTop: 14 }}>
        <div style={{ padding: '12px 16px 0' }}>
          <div className="sec-title">Contenido</div>
        </div>
        {d.contenido.length === 0 ? (
          <Vacio mensaje="La guia va vacia" icono="📦" />
        ) : (
          <table data-prueba="tabla-contenido">
            <thead>
              <tr>
                <th>Numero de parte</th>
                <th>Articulo</th>
                <th className="num">Cantidad</th>
                <th>Series</th>
              </tr>
            </thead>
            <tbody>
              {d.contenido.map((r) => (
                <tr key={r.numero_parte}>
                  <td className="folio">{r.numero_parte}</td>
                  <td>{r.articulo}</td>
                  <td className="num">{entero(r.cantidad)}</td>
                  <td>
                    {r.series.length === 0 ? (
                      <span className="muted">no serializado</span>
                    ) : (
                      <div className="chips">
                        {r.series.map((s) => (
                          <Link key={s} className="chip" to={`/rastreo?q=${encodeURIComponent(s)}`}>
                            {s}
                          </Link>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
